// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ImportHistory.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import { observable, computed, action } from 'mobx';
import { forEach, isEmpty, every, includes, map, filter, find, last } from 'lodash/fp';
import ImportHistoryFileContent from './Izenda.ImportHistoryFileContent';
import {
  DEFAULT_ROW_HEIGHT,
  DEFAULT_REST_HEIGHT
} from 'logic/data/Izenda.ImportHistoryConstants';
import { PAGING_SIZE_OPTIONS as PAGE_SIZES } from 'logic/data/Izenda.UIConstants';

export default class ImportHistory {
  @observable historyFiles: Array<ImportHistoryFileContent> = [];
  @observable deletedId: string = '';

  // For paging
  @observable tableHeight: number = 0;
  @observable pageIndex: number = 1;
  @observable total: number = 0;
  @observable pageSize: number = 10;
  @observable sortOrders: Array<any> = []; // Use to send through APIs
  @observable isLastPage: boolean = true;
  @observable sortInfo: any = {}; // Use in UI

  /**
   * Set table height
   */
  @action
  setTableHeight(tableHeight): void {
    this.tableHeight = tableHeight;
  }

  /**
   * Find suitable page index
   * @returns {number}
   */
  @action
  findSuitablePageIndex(): number {
    const maxRows = (this.tableHeight - DEFAULT_REST_HEIGHT) / DEFAULT_ROW_HEIGHT;
    return find(size => maxRows <= size, PAGE_SIZES) || last(PAGE_SIZES);
  }

  /**
   * Get if all are checked
   * @returns {Boolean}
   */
  @computed
  get isAllChecked(): boolean {
    return !isEmpty(this.historyFiles) && every({ isSelected: true }, this.historyFiles);
  }

  /**
   * Get if not any checked, to disable button delete
   */
  @computed
  get isNoChecked(): boolean {
    return isEmpty(this.historyFiles) || every({ isSelected: false }, this.historyFiles);
  }

  /**
   * Reset page info when change tenant
   */
  @action
  resetPageInfo(): void {
    this.pageIndex = 1;
    this.total = 0;
    this.pageSize = this.findSuitablePageIndex();
    this.sortInfo = {};
    this.sortOrders = [];
    this.isLastPage = true;

    this.deletedId = '';
    this.resetHistoryFiles();
  }

  /**
   * Check if historyFiles is empty, to disable button delete or not
   * @returns {Boolean}
   */
  @computed
  get isEmptyHistories(): boolean {
    return isEmpty(this.historyFiles);
  }

  /**
   * Reset after reloading
   */
  @action
  resetHistoryFiles(): void {
    this.historyFiles = [];
  }

  /**
   * Populate history files
   * @param {Object} response
   */
  @action
  populate(response: any): void {
    const { result, total, isLastPage } = response;

    this.total = total;
    this.isLastPage = isLastPage;

    const selectedIds = isEmpty(this.deletedId) ? [] : filter(id => id != this.deletedId, this.getSelectedIds());

    this.historyFiles = map(
      historyFile => new ImportHistoryFileContent(historyFile, includes(historyFile.id, selectedIds)),
      result
    );

    this.deletedId = '';
  }

  /**
   * Update check all items
   */
  @action
  updateCheckAll(): void {
    const shouldAllChecked = !this.isAllChecked; // Keep the next status of check all, hard-set to every item

    forEach(historyFile => {
      historyFile.updateSelected(shouldAllChecked);
    }, this.historyFiles);
  }

  /**
   * Get data to request page
   * @param {String} tenantId
   * @returns {Object}
   */
  @action
  getRequestPage(tenantId: string): any {
    const { pageIndex, pageSize, sortOrders } = this;
    return { tenantId, pageIndex, pageSize, sortOrders };
  }

  /**
   * Update page index selected
   * @param {Number} pageIndex
   */
  @action
  updatePageIndex(pageIndex: number): void {
    this.pageIndex = pageIndex;
  }

  /**
   * Update page size of table
   * @param {Number} pageSize
   */
  @action
  updatePageSize(pageSize: number): void {
    this.pageSize = pageSize;
    this.pageIndex = 1;
  }

  /**
   * Get selected histories'id
   * @returns {Array<String>}
   */
  @action
  getSelectedIds(): Array<string> {
    let selectedIds = [];

    forEach(historyFile => {
      if (historyFile.isSelected) {
        selectedIds.push(historyFile.id);
      }
    }, this.historyFiles);

    return selectedIds;
  }

  /**
   * Set sort info in UI, but only SET ONE at time
   * @param {String} columnKey
   * @param {String} sortDir
   */
  @action
  setSortInfo(columnKey: string, sortDir: string): void {
    this.sortInfo = {};
    this.sortOrders = [];

    if (!isEmpty(sortDir)) {
      this.sortInfo[columnKey] = sortDir; // Single-sort
      this.sortOrders.push({
        key: columnKey,
        descending: sortDir === 'DESC' ? true : false
      });
    }
  }
}



// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.ImportHistory.js